import { getKubientConfig } from './getKubientConfig';
import { consoleTimer, TimerCommand } from './timer';

/**
 * @param kubientId ID used by kubient to determine what ads to serve
 * @param {number} kubientTimeout
 */
export function PlayKubientAd({ kubientId, kubientTimeout }) {
    /***
     * Indicates if the kubient completed handler is running.
     * @type {boolean}
     */
    let videoCompletedHandlerRunning = false;

    //Kubient Lock Screen Background
    let kubientBackground = document.createElement('DIV');
    kubientBackground.setAttribute('id', 'kubientBackground');

    //Container that contains the kubient ad
    let kubientContainer = document.createElement('DIV');
    kubientContainer.setAttribute('id', 'kubientContainer');

    //Container that kubient player renders into
    let kubientRenderRegion = document.createElement('DIV');
    kubientRenderRegion.setAttribute('id', kubientId);
    kubientRenderRegion.classList.add('kubientad');

    kubientContainer.appendChild(kubientRenderRegion);

    document.body.appendChild(kubientBackground);
    document.body.appendChild(kubientContainer);

    /***
     * This removed html elements which are used to contain the kubient player
     */
    const removeKubientDomElements = (() => {
        /***
         * We only want to remove kubient elements once. Promises return resolve or reject only once,
         * The code will continue to run after this resolution occurs and the removeKubientDomElements
         * could potentially be called more than once, which will lead to additional logged errors.
         * @type {boolean}
         */
        let kubientCleanedUp = false;

        return () => {
            if (!kubientCleanedUp) {
                kubientCleanedUp = true;
                kubientContainer.remove();
                kubientBackground.remove();
                document.body.classList.remove('lockscreen');
            }
        };
    })();

    const kubientResponseTimer = consoleTimer('Kubient Response Time');

    /***
     * This promise resolved when and ad has played, or no ad has played.
     */
    return new Promise((resolve, reject) => {
        kubientResponseTimer(TimerCommand.start);

        let kubientConfig = getKubientConfig(kubientId);

        var adPlayerPro = AdPlayerPro(kubientId).setup(kubientConfig);

        /***
         * Just in case no ad is delivered within a set timeframe we shut down kubient so that
         * the stream can continue to play.
         * @type {number}
         */
        let expireTimeout = window.setTimeout(() => {
            kubientResponseTimer(TimerCommand.stop);
            removeKubientDomElements();
            reject({
                message: `timed out down due to ${kubientTimeout /
                    1500} second timeout`
            });
        }, kubientTimeout);

        /***
         * This handler is called if there is an error playing the ad. It was communicated to
         * us that this would get called if no ad was server; however, we are observing that the
         * AdStopped event is fired. We are retaining this just in case there are conditions
         * where this gets called instead of the AdStopped event.
         */
        adPlayerPro.on('AdError', error => {
            kubientResponseTimer(TimerCommand.stop);

            if (expireTimeout) {
                window.clearTimeout(expireTimeout);
            }

            removeKubientDomElements();
            reject({ message: 'ad has not been loaded...' });
        });
        /***
         * This handler is called when and ad has completed playing. We hide kubient elements
         * after 1.5 seconds so that the audio completes playing an the video close animation
         * has time to run.
         */
        adPlayerPro.on('AdVideoComplete', () => {
            videoCompletedHandlerRunning = true;
            window.setTimeout(() => {
                removeKubientDomElements();
                resolve({
                    message: 'Ad has successfully finished playing'
                });
            }, 1500);
        });

        /***
         * This handler is executed when an ad starts. We lock the screen and display the kubient elements
         */
        adPlayerPro.on('AdStarted', () => {
            kubientResponseTimer(TimerCommand.stop);
            if (expireTimeout) {
                window.clearTimeout(expireTimeout);
            }

            document.body.classList.add('lockscreen');
            kubientBackground.style.visibility = 'visible';
            kubientContainer.style.visibility = 'visible';
        });

        /***
         * This handler is executed when a user click the ad.. We abruptly end the ad an the clickthrough
         * url is opened. This function can be modified to continue the playing of the video in the background
         */
        adPlayerPro.on('AdClickThru', () => {
            if (expireTimeout) {
                window.clearTimeout(expireTimeout);
            }

            removeKubientDomElements();
            resolve({
                message: 'Ad has successfully finished playing'
            });
        });

        /***
         * This handler is executed when the ad is stopped due to skipping or non existence.
         */
        adPlayerPro.on('AdStopped', () => {
            kubientResponseTimer(TimerCommand.stop);
            if (expireTimeout) {
                window.clearTimeout(expireTimeout);
            }

            // if the video completed handler is currently running we will allow it to remove the kubient elements
            if (!videoCompletedHandlerRunning) {
                removeKubientDomElements();
                resolve({
                    message: 'Ad was skipped'
                });
            }
        });
    });
}
